/**
 * Global Color Constants
 * Centralized color definitions to avoid hardcoding colors throughout the app
 */

// Primary Brand Colors
export const COLORS = {
  // Primary Colors
  PRIMARY: {
    MAIN: '#030978',
    LIGHT: '#3366a3',
    DARK: '#002d5c',
    CONTRAST: '#ffffff',
  },

  // Secondary Colors  
  SECONDARY: {
    MAIN: '#afafaf',
    LIGHT: '#d2d2d2',
    DARK: '#8c8c8c',
    CONTRAST: '#000000',
  },

  // Neutral Colors
  NEUTRAL: {
    WHITE: '#ffffff',
    BLACK: '#000000',
    GREY_50: '#fafafa',
    GREY_100: '#f5f5f5',
    GREY_200: '#eeeeee',
    GREY_300: '#e0e0e0',
    GREY_400: '#bdbdbd',
    GREY_500: '#9e9e9e',
    GREY_600: '#757575',
    GREY_700: '#616161',
    GREY_800: '#424242',
    GREY_900: '#212121',
  },

  // Background Colors
  BACKGROUND: {
    LIGHT: {
      DEFAULT: '#f0f2f5',
      PAPER: '#ffffff',
      ELEVATED: '#fafafa',
    },
    DARK: {
      DEFAULT: '#141414',
      PAPER: '#1f1f1f',
      ELEVATED: '#262626',
    },
  },

  // Text Colors
  TEXT: {
    LIGHT: {
      PRIMARY: '#262626',
      SECONDARY: '#8c8c8c',
      DISABLED: '#bdbdbd',
    },
    DARK: {
      PRIMARY: '#ffffff',
      SECONDARY: '#bfbfbf',
      DISABLED: '#757575',
    },
  },

  // Status Colors
  STATUS: {
    SUCCESS: {
      MAIN: '#52c41a',
      LIGHT: '#73d13d',
      DARK: '#389e0d',
    },
    WARNING: {
      MAIN: '#faad14',
      LIGHT: '#ffc53d',
      DARK: '#d48806',
    },
    ERROR: {
      MAIN: '#ff4d4f',
      LIGHT: '#ff7875',
      DARK: '#cf1322',
    },
    INFO: {
      MAIN: '#1890ff',
      LIGHT: '#40a9ff',
      DARK: '#096dd9',
    },
  },

  // Border Colors
  BORDER: {
    LIGHT: 'rgba(0, 0, 0, 0.08)',
    DARK: 'rgba(255, 255, 255, 0.08)',
    HOVER_LIGHT: 'rgba(0, 0, 0, 0.12)',
    HOVER_DARK: 'rgba(255, 255, 255, 0.12)',
  },

  // Shadow Colors
  SHADOW: {
    LIGHT: {
      DEFAULT: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
      SUBTLE: 'rgba(0, 0, 0, 0.03) 0px 4px 16px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
      STRONG: 'rgba(0, 0, 0, 0.08) 0px 8px 32px 0px, rgba(0, 0, 0, 0.12) 0px 0px 0px 1px',
    },
    DARK: {
      DEFAULT: 'rgba(255, 255, 255, 0.05) 0px 6px 24px 0px, rgba(255, 255, 255, 0.08) 0px 0px 0px 1px',
      SUBTLE: 'rgba(255, 255, 255, 0.03) 0px 4px 16px 0px, rgba(255, 255, 255, 0.06) 0px 0px 0px 1px',
      STRONG: 'rgba(255, 255, 255, 0.08) 0px 8px 32px 0px, rgba(255, 255, 255, 0.12) 0px 0px 0px 1px',
    },
  },

  // Focus Colors
  FOCUS: {
    LIGHT: 'rgba(24, 144, 255, 0.2)',
    DARK: 'rgba(144, 202, 249, 0.2)',
  },

  // Overlay Colors
  OVERLAY: {
    LIGHT: 'rgba(0, 0, 0, 0.04)',
    DARK: 'rgba(255, 255, 255, 0.04)',
  },
} as const;

// Theme-aware color getters
export const getThemeColors = (mode: 'light' | 'dark') => ({
  background: mode === 'light' ? COLORS.BACKGROUND.LIGHT : COLORS.BACKGROUND.DARK,
  text: mode === 'light' ? COLORS.TEXT.LIGHT : COLORS.TEXT.DARK,
  border: mode === 'light' ? COLORS.BORDER.LIGHT : COLORS.BORDER.DARK,
  borderHover: mode === 'light' ? COLORS.BORDER.HOVER_LIGHT : COLORS.BORDER.HOVER_DARK,
  shadow: mode === 'light' ? COLORS.SHADOW.LIGHT : COLORS.SHADOW.DARK,
  focus: mode === 'light' ? COLORS.FOCUS.LIGHT : COLORS.FOCUS.DARK,
  overlay: mode === 'light' ? COLORS.OVERLAY.LIGHT : COLORS.OVERLAY.DARK,
});

// Professional color combinations for ColorPicker
export const PROFESSIONAL_COLOR_COMBINATIONS = [
  // Professional Blues
  { primary: '#01427a', secondary: '#6c757d', name: 'Classic Navy' },
  { primary: '#1e3a8a', secondary: '#64748b', name: 'Corporate Blue' },
  { primary: '#0f4c75', secondary: '#718096', name: 'Deep Ocean' },
  { primary: '#1d4ed8', secondary: '#8b949e', name: 'Royal Blue' },
  
  // Professional Greens  
  { primary: '#047857', secondary: '#6b7280', name: 'Forest Green' },
  { primary: '#059669', secondary: '#737373', name: 'Emerald Pro' },
  { primary: '#166534', secondary: '#71717a', name: 'Pine Green' },
  { primary: '#0d9488', secondary: '#78716c', name: 'Teal Pro' },
  
  // Professional Greys & Neutrals
  { primary: '#374151', secondary: '#9ca3af', name: 'Charcoal' },
  { primary: '#4b5563', secondary: '#a1a1aa', name: 'Steel Grey' },
  { primary: '#1f2937', secondary: '#9ca3af', name: 'Dark Slate' },
  { primary: '#6b7280', secondary: '#d1d5db', name: 'Professional' },
  
  // Sophisticated Purples
  { primary: '#5b21b6', secondary: '#78716c', name: 'Violet Pro' },
  { primary: '#7c3aed', secondary: '#6b7280', name: 'Royal Purple' },
  { primary: '#6366f1', secondary: '#71717a', name: 'Indigo Pro' },
  
  // Professional Burgundy & Browns
  { primary: '#7f1d1d', secondary: '#78716c', name: 'Burgundy' },
  { primary: '#92400e', secondary: '#6b7280', name: 'Amber Pro' },
  { primary: '#8b5cf6', secondary: '#737373', name: 'Lavender Pro' },
  
  // Educational Institution Colors
  { primary: '#1e40af', secondary: '#64748b', name: 'Academic Blue' },
  { primary: '#059669', secondary: '#71717a', name: 'Education Green' },
  { primary: '#dc2626', secondary: '#6b7280', name: 'Institution Red' },
  { primary: '#7c2d12', secondary: '#78716c', name: 'Scholar Brown' },
  { primary: '#1e3a8a', secondary: '#78716c', name: 'University Blue' },
  { primary: '#065f46', secondary: '#737373', name: 'Campus Green' },
] as const;

// CSS Variables for consistent usage
export const CSS_VARIABLES = {
  // Border radius
  BORDER_RADIUS: {
    SMALL: '4px',
    MEDIUM: '6px', 
    LARGE: '8px',
    ROUND: '50%',
  },

  // Transitions
  TRANSITIONS: {
    FAST: 'all 0.15s ease',
    DEFAULT: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
    SLOW: 'all 0.5s ease',
  },

  // Z-index layers
  Z_INDEX: {
    DROPDOWN: 1000,
    STICKY: 1020,
    FIXED: 1030,
    MODAL_BACKDROP: 1040,
    MODAL: 1050,
    POPOVER: 1060,
    TOOLTIP: 1070,
  },
} as const;

export default COLORS;